import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSort, SortHeader } from "../ui/sortable";
import { Link } from "react-router-dom";
import { ShieldCheck, ChevronRight } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { cn } from "../../lib/utils";
import { useHospitalColors } from "../../utils/hospitalColors";
import { useTranslation } from "../../i18n";
import {
  fetchComplianceHistory,
  fetchComplianceStatus,
  type ComplianceHistoryResponse,
  type ComplianceRate,
  type HospitalComplianceRate,
} from "../../services/compliance";

/**
 * 2-hour compliance rate — the share of active checkpoints with a scan in the
 * last 2 hours. A checkpoint that is overdue OR has no scan on record counts as
 * non-compliant. Reporting only: like the rest of the compliance surface this
 * states what the record shows; it does not assign work.
 */

const BRAND = "#3B7BBE";

const fmtPct = (v: number | null | undefined) => (v == null ? "—" : `${v.toFixed(1)}%`);

/** Headline: big rate + "N of M checkpoints scanned in the last 2 hours". */
export function ComplianceRateSummary({
  rate,
  compact = false,
  linkTo,
}: {
  rate: ComplianceRate | null | undefined;
  compact?: boolean;
  linkTo?: string;
}) {
  const { t, lang } = useTranslation();
  const n = (v: number) => v.toLocaleString(lang);
  const body = (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-6 gap-y-3 rounded-xl border border-gray-200 bg-white shadow-sm",
        compact ? "px-4 py-3" : "px-6 py-5",
        linkTo && "transition-colors hover:border-[#3B7BBE]/50 hover:bg-[#3B7BBE]/5"
      )}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#3B7BBE]/10 text-[#3B7BBE]">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t("compliance.rate2hTitle", "2-hour compliance")}
          </div>
          <div className={cn("font-bold tabular-nums text-gray-900", compact ? "text-2xl" : "text-3xl")}>
            {fmtPct(rate?.rate)}
          </div>
        </div>
      </div>
      {rate && (
        <div className="min-w-0 flex-1 text-sm text-gray-600">
          <div>
            {t("compliance.rate2hDetail", "{a} of {b} checkpoints scanned in the last 2 hours")
              .replace("{a}", n(rate.compliant))
              .replace("{b}", n(rate.total))}
          </div>
          <div className="mt-0.5 text-gray-500">
            {t("compliance.rate2hNon", "{n} non-compliant: {o} overdue, {s} with no scan on record")
              .replace("{n}", n(rate.non_compliant))
              .replace("{o}", n(rate.overdue))
              .replace("{s}", n(rate.never_scanned))}
          </div>
          {/* Square-ended bar, same treatment as the Live Activity bar plot. */}
          <div className="relative mt-2 h-3 w-full max-w-md bg-gray-100" aria-hidden>
            <div
              className="absolute inset-y-0 left-0"
              style={{ width: `${Math.max(0, Math.min(100, rate.rate ?? 0))}%`, backgroundColor: BRAND }}
            />
          </div>
        </div>
      )}
      {linkTo && (
        <span className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-[#3B7BBE]">
          {t("compliance.viewCompliance", "View compliance")}
          <ChevronRight className="h-4 w-4" />
        </span>
      )}
    </div>
  );
  return linkTo ? <Link to={linkTo} className="block">{body}</Link> : body;
}

/** Collapsible card shell — same header pattern as the other Compliance sections. */
function Section({
  title,
  right,
  open,
  onToggle,
  children,
}: {
  title: React.ReactNode;
  right?: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className={cn("flex flex-wrap items-center justify-between gap-3 px-6 py-4", open && "border-b border-gray-200")}>
        <button onClick={onToggle} className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left">
          <h2 className="font-semibold text-gray-800">{title}</h2>
          <span className="text-xs font-medium text-gray-500">
            {open ? t("common.collapse", "Collapse") : t("common.expand", "Expand")}
          </span>
        </button>
        {open && right}
      </div>
      {open && children}
    </div>
  );
}

/**
 * Per-hospital current 2-hour rate. Sortable by any column (highest rate first
 * by default) and collapsible, like the other tables on the Compliance page.
 */
export function HospitalRateTable({ rows }: { rows: HospitalComplianceRate[] | undefined }) {
  const { t, lang } = useTranslation();
  // Same hospital → colour mapping as the Live Activity comparison plot, Scan
  // Map and Calendar, so a site reads with one colour everywhere.
  const hospitalColors = useHospitalColors();
  const [open, setOpen] = useState(true);
  const data = useMemo(() => (rows ?? []).filter((r) => r.total > 0), [rows]);
  const { sorted, sort, toggle } = useSort(
    data,
    {
      hospital: (r) => r.hospital_name,
      rate: (r) => r.rate,
      scanned: (r) => r.compliant,
      overdue: (r) => r.overdue,
      never: (r) => r.never_scanned,
    },
    { key: "rate", dir: "desc" }
  );
  if (data.length <= 1) return null;
  return (
    <Section
      title={
        <>
          {t("compliance.rateByHospitalTitle", "2-Hour Rate by Hospital")}
          <span className="ml-2 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{data.length}</span>
        </>
      }
      open={open}
      onToggle={() => setOpen((o) => !o)}
    >
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <SortHeader label={t("domain.hospital", "Hospital")} k="hospital" sort={sort} onToggle={toggle} />
              <SortHeader label={t("compliance.rate2hCol", "2-hour rate")} k="rate" sort={sort} onToggle={toggle} />
              <SortHeader label={t("compliance.scanned2h", "Scanned in 2h")} k="scanned" sort={sort} onToggle={toggle} right />
              <SortHeader label={t("compliance.overdue", "Overdue")} k="overdue" sort={sort} onToggle={toggle} right />
              <SortHeader label={t("compliance.neverScanned", "No Scan on Record")} k="never" sort={sort} onToggle={toggle} right />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.map((r) => (
              <tr key={r.hospital_id}>
                <td className="px-4 py-2 font-medium text-gray-800">{r.hospital_name ?? "—"}</td>
                <td className="px-4 py-2">
                  {/* Square-ended horizontal bar in the hospital's colour with the
                      value at the end — matches the Live Activity bar plot. */}
                  <div className="flex items-center gap-2">
                    <div className="relative h-5 w-48 bg-gray-100" aria-hidden>
                      <div
                        className="absolute inset-y-0 left-0"
                        style={{
                          width: `${Math.max(0, Math.min(100, r.rate ?? 0))}%`,
                          backgroundColor: hospitalColors.colorFor({ id: r.hospital_id, name: r.hospital_name }),
                          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.6)",
                        }}
                      />
                    </div>
                    <span className="w-14 tabular-nums font-semibold text-gray-900">{fmtPct(r.rate)}</span>
                  </div>
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                  {r.compliant.toLocaleString(lang)} / {r.total.toLocaleString(lang)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-700">{r.overdue.toLocaleString(lang)}</td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-700">{r.never_scanned.toLocaleString(lang)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

const RANGES = [
  { key: "1d", days: 1, label: "Last 24 hours" },
  { key: "7d", days: 7, label: "Last 7 days" },
  { key: "30d", days: 30, label: "Last 30 days" },
] as const;
type RangeKey = (typeof RANGES)[number]["key"];

function etDay(offsetDays = 0): string {
  const d = new Date(Date.now() + offsetDays * 86_400_000);
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
}

/** Trend of the 2-hour rate from the 15-minute snapshots. Single series → no legend. */
export function ComplianceTrend({ hospitalId }: { hospitalId?: string }) {
  const { t, lang } = useTranslation();
  const [open, setOpen] = useState(true);
  const hospitalColors = useHospitalColors();
  const [range, setRange] = useState<RangeKey>("7d");
  const [data, setData] = useState<ComplianceHistoryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Series the viewer has switched off via the legend ("total" or a hospital id).
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  // Series under the pointer in the legend — everything else is dimmed.
  const [focus, setFocus] = useState<string | null>(null);
  // Series highlighted by clicking its line (sticky until clicked again or cleared).
  const [pinned, setPinned] = useState<string | null>(null);
  // A click on a line also bubbles up to the chart's own click handler, which
  // clears the highlight for clicks on open space. This flag lets the chart
  // handler tell the two apart.
  const lineClicked = useRef(false);
  const togglePin = (key: string) => {
    lineClicked.current = true;
    setPinned((p) => (p === key ? null : key));
  };
  const onChartClick = () => {
    if (lineClicked.current) {
      lineClicked.current = false;
      return;
    }
    setPinned(null); // clicked open space → same as "Clear highlight"
  };

  const load = useCallback(async () => {
    const days = RANGES.find((r) => r.key === range)!.days;
    setLoading(true);
    try {
      const res = await fetchComplianceHistory({
        hospitalId,
        // 24h view spans yesterday+today so it always has a full day of hours.
        start: etDay(days === 1 ? -1 : -(days - 1)),
        end: etDay(0),
        bucket: days <= 7 ? "hour" : "day",
      });
      setData(res);
      setError(null);
    } catch (e: any) {
      setError(e?.message || "Could not load compliance history");
    } finally {
      setLoading(false);
    }
  }, [hospitalId, range]);

  useEffect(() => {
    void load();
  }, [load]);

  const cutoff = range === "1d" ? Date.now() - 86_400_000 : 0;
  const toTs = (period: string) => new Date(`${period}:00`).getTime();

  // Total line (all hospitals in scope combined).
  const points = useMemo(
    () =>
      (data?.series ?? [])
        .map((r) => ({ ...r, ts: toTs(r.period) }))
        .filter((r) => r.avg_rate != null && r.ts >= cutoff),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, range]
  );

  // One series per hospital, coloured like everywhere else on the platform.
  const hospitalSeries = useMemo(
    () =>
      (data?.series_by_hospital ?? [])
        .map((h) => ({
          key: `h_${h.hospital_id}`,
          id: h.hospital_id,
          name: h.hospital_name ?? "—",
          color: hospitalColors.colorFor({ id: h.hospital_id, name: h.hospital_name }),
          points: h.points.filter((p) => p.avg_rate != null && toTs(p.period) >= cutoff),
          avg:
            h.points.length > 0
              ? h.points.reduce((s, p) => s + (p.avg_rate ?? 0) * p.samples, 0) / Math.max(1, h.points.reduce((s, p) => s + p.samples, 0))
              : null,
        }))
        .filter((h) => h.points.length > 0)
        .sort((a, b) => a.name.localeCompare(b.name)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, range, hospitalColors]
  );

  // With one hospital selected the "total" IS that hospital — show one line.
  const showTotal = !hospitalId && hospitalSeries.length !== 1;

  // Recharts wants one row per x value with a column per series.
  const rows = useMemo(() => {
    const byTs = new Map<number, Record<string, number | null>>();
    const row = (ts: number) => {
      let r = byTs.get(ts);
      if (!r) {
        r = { ts };
        byTs.set(ts, r);
      }
      return r;
    };
    for (const p of points) row(p.ts).total = p.avg_rate;
    for (const h of hospitalSeries) for (const p of h.points) row(toTs(p.period))[h.key] = p.avg_rate;
    return [...byTs.values()].sort((a, b) => (a.ts as number) - (b.ts as number));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, hospitalSeries]);

  // X-axis ticks placed on the clock rather than wherever the chart library
  // lands: every midnight carries the date, and the hours in between carry
  // the time (24h: every 3 hours; 7 days: 6 AM / 12 PM / 6 PM; 30 days:
  // every few days, dates only). Avoids the old "Oct 3, Oct 3, Oct 3" axis.
  // The 7-day view drops to dates only when the chart is narrow (phones).
  const chartBoxRef = useRef<HTMLDivElement | null>(null);
  const [chartWidth, setChartWidth] = useState(1000);
  useEffect(() => {
    const el = chartBoxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => setChartWidth(entries[0]?.contentRect.width || 1000));
    ro.observe(el);
    return () => ro.disconnect();
  });
  const dateFmt = useMemo(() => new Intl.DateTimeFormat(lang, { month: "short", day: "numeric" }), [lang]);
  const hourFmt = useMemo(() => new Intl.DateTimeFormat(lang, { hour: "numeric" }), [lang]);
  const xTicks = useMemo(() => {
    if (!rows.length) return [] as number[];
    const min = rows[0].ts as number;
    const max = rows[rows.length - 1].ts as number;
    const spanDays = (max - min) / 86_400_000;
    const stepHours =
      data?.bucket === "day"
        ? 24 * Math.max(1, Math.ceil(spanDays / (chartWidth < 700 ? 5 : 10)))
        : spanDays <= 1.6
          ? (chartWidth < 700 ? 6 : 3)
          : chartWidth < 700
            ? 24
            : 12;
    const out: number[] = [];
    const d = new Date(min);
    d.setMinutes(0, 0, 0);
    // Snap the first tick up to the next multiple of the step (local clock).
    while ((d.getHours() % Math.min(stepHours, 24)) !== 0 || d.getTime() < min) d.setHours(d.getHours() + 1);
    if (stepHours >= 24) {
      // Day ticks: anchor on midnights, keep every Nth day.
      const n = stepHours / 24;
      let i = 0;
      for (; d.getTime() <= max; d.setDate(d.getDate() + 1), i++) if (i % n === 0) out.push(d.getTime());
    } else {
      for (; d.getTime() <= max; d.setHours(d.getHours() + stepHours)) out.push(d.getTime());
    }
    return out;
  }, [rows, data?.bucket, chartWidth]);
  const isMidnight = (v: number) => {
    const t = new Date(v);
    return t.getHours() === 0 && t.getMinutes() === 0;
  };
  const XTick = ({ x, y, payload }: any) => {
    const v = Number(payload?.value);
    const day = data?.bucket === "day" || isMidnight(v);
    return (
      <text x={x} y={y + 12} textAnchor="middle" fontSize={11} fill={day ? "#374151" : "#9ca3af"} fontWeight={day ? 600 : 400}>
        {day ? dateFmt.format(new Date(v)) : hourFmt.format(new Date(v))}
      </text>
    );
  };
  const tipFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(lang, data?.bucket === "day" ? { weekday: "short", month: "short", day: "numeric" } : { weekday: "short", month: "short", day: "numeric", hour: "numeric" }),
    [lang, data?.bucket]
  );

  const avg =
    points.length > 0
      ? Math.round((points.reduce((s, p) => s + (p.avg_rate ?? 0) * p.samples, 0) / points.reduce((s, p) => s + p.samples, 0)) * 10) / 10
      : null;

  const toggle = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const setAllHospitals = (visible: boolean) =>
    setHidden((prev) => {
      const next = new Set(prev);
      for (const h of hospitalSeries) {
        if (visible) next.delete(h.key);
        else next.add(h.key);
      }
      return next;
    });

  // Legend hover wins while it lasts; otherwise the clicked (pinned) line.
  const highlighted = focus ?? pinned;
  const opacityFor = (key: string) => (highlighted && highlighted !== key ? 0.15 : 1);
  const visibleHospitals = hospitalSeries.filter((h) => !hidden.has(h.key));
  const TOTAL_INK = "#1F2937";

  return (
    <Section
      open={open}
      onToggle={() => setOpen((o) => !o)}
      title={
        <>
          {t("compliance.rateTrendTitle", "2-Hour Compliance Over Time")}
          <span className="ml-2 text-xs font-normal text-gray-500">
            {avg != null
              ? t("compliance.rateTrendAvg", "Average {v} for this period").replace("{v}", fmtPct(avg))
              : t("compliance.rateTrendSub", "Recorded every 15 minutes")}
          </span>
        </>
      }
      right={
        <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={cn(
                "rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
                range === r.key ? "bg-white text-[#3B7BBE] shadow-sm" : "text-gray-600 hover:text-gray-900"
              )}
            >
              {t(`compliance.range.${r.key}`, r.label)}
            </button>
          ))}
        </div>
      }
    >
      <div className="p-5">
        {error ? (
          <p className="py-10 text-center text-sm text-red-700">{error}</p>
        ) : loading && !data ? (
          <p className="py-10 text-center text-sm text-gray-500">{t("common.loading", "Loading…")}</p>
        ) : rows.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray-500">
            {t(
              "compliance.rateTrendEmpty",
              "No history yet for this period. The rate is recorded every 15 minutes from when this feature went live."
            )}
          </p>
        ) : (
          <>
            {/* Legend — click to show/hide a line, hover to highlight it. */}
            <div className="mb-3 flex flex-wrap items-center gap-1.5" onMouseLeave={() => setFocus(null)}>
              {showTotal && (
                <LegendChip
                  label={t("compliance.allHospitalsTotal", "All hospitals")}
                  color={TOTAL_INK}
                  thick
                  off={hidden.has("total")}
                  onClick={() => toggle("total")}
                  onHover={() => setFocus("total")}
                />
              )}
              {hospitalSeries.map((h) => (
                <LegendChip
                  key={h.key}
                  label={h.name}
                  color={h.color}
                  off={hidden.has(h.key)}
                  onClick={() => toggle(h.key)}
                  onHover={() => setFocus(h.key)}
                />
              ))}
              {pinned && (
                <button
                  className="ml-1 rounded-md border border-[#3B7BBE]/40 bg-[#3B7BBE]/10 px-2 py-1 text-xs font-medium text-[#3B7BBE] hover:bg-[#3B7BBE]/20"
                  onClick={() => setPinned(null)}
                >
                  {t("compliance.clearHighlight", "Clear highlight")}
                </button>
              )}
              {hospitalSeries.length > 1 && (
                <span className="ml-1 flex gap-2 text-xs font-medium">
                  <button className="text-[#3B7BBE] hover:underline" onClick={() => setAllHospitals(true)}>
                    {t("compliance.showAll", "Show all")}
                  </button>
                  <button className="text-[#3B7BBE] hover:underline" onClick={() => setAllHospitals(false)}>
                    {t("compliance.hideHospitals", "Hide hospitals")}
                  </button>
                </span>
              )}
            </div>

            <div className="h-72 w-full" ref={chartBoxRef}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={rows}
                  margin={{ top: 8, right: 12, bottom: 0, left: -12 }}
                  onClick={onChartClick}
                >
                  <CartesianGrid stroke="#eef0f3" vertical={false} />
                  <XAxis
                    dataKey="ts"
                    type="number"
                    scale="time"
                    domain={["dataMin", "dataMax"]}
                    ticks={xTicks}
                    interval={0}
                    tick={<XTick />}
                    axisLine={{ stroke: "#e5e7eb" }}
                    tickLine={false}
                    minTickGap={6}
                  />
                  <YAxis
                    domain={[0, 100]}
                    ticks={[0, 25, 50, 75, 100]}
                    tickFormatter={(v) => `${v}%`}
                    tick={{ fontSize: 11, fill: "#6b7280" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ stroke: "#9ca3af", strokeWidth: 1 }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const r: any = payload[0].payload;
                      const lines = visibleHospitals
                        .map((h) => ({ key: h.key, name: h.name, color: h.color, v: r[h.key] as number | null | undefined }))
                        .filter((x) => x.v != null)
                        .sort((a, b) => (b.v as number) - (a.v as number));
                      return (
                        <div className="max-w-xs rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-md">
                          <div className="mb-1 font-semibold text-gray-800">{tipFmt.format(new Date(r.ts))}</div>
                          {showTotal && !hidden.has("total") && r.total != null && (
                            <div className="flex items-center justify-between gap-4 font-semibold text-gray-900">
                              <span>{t("compliance.allHospitalsTotal", "All hospitals")}</span>
                              <span className="tabular-nums">{fmtPct(r.total)}</span>
                            </div>
                          )}
                          {lines.map((x) => (
                            <div
                              key={x.name}
                              className={cn(
                                "flex items-center justify-between gap-4",
                                highlighted === x.key ? "font-semibold text-gray-900" : "text-gray-700"
                              )}
                            >
                              <span className="flex min-w-0 items-center gap-1.5">
                                <span className="h-2 w-2 flex-shrink-0" style={{ backgroundColor: x.color }} />
                                <span className="truncate">{x.name}</span>
                              </span>
                              <span className="tabular-nums">{fmtPct(x.v)}</span>
                            </div>
                          ))}
                        </div>
                      );
                    }}
                  />
                  {hospitalSeries.map((h) =>
                    hidden.has(h.key) ? null : (
                      <Line
                        key={h.key}
                        type="monotone"
                        dataKey={h.key}
                        name={h.name}
                        stroke={h.color}
                        strokeWidth={highlighted === h.key ? 3.5 : hospitalId ? 2 : 1.5}
                        strokeOpacity={opacityFor(h.key)}
                        dot={false}
                        activeDot={{ r: 4, strokeWidth: 2, fill: "#fff" }}
                        connectNulls
                        isAnimationActive={false}
                      />
                    )
                  )}
                  {showTotal && !hidden.has("total") && (
                    <Line
                      type="monotone"
                      dataKey="total"
                      onClick={() => togglePin("total")}
                      style={{ cursor: "pointer" }}
                      name="All hospitals"
                      stroke={TOTAL_INK}
                      strokeWidth={3}
                      strokeOpacity={opacityFor("total")}
                      dot={false}
                      activeDot={{ r: 5, strokeWidth: 2, fill: "#fff" }}
                      connectNulls
                      isAnimationActive={false}
                    />
                  )}
                  {/* Wide transparent copies of each hospital line: an easy click
                      target (the visible lines are only 1.5px). Clicking one
                      highlights that hospital; clicking it again clears it. The
                      hover tooltip with every hospital's value is unaffected. */}
                  {hospitalSeries.map((h) =>
                    hidden.has(h.key) ? null : (
                      <Line
                        key={`hit_${h.key}`}
                        type="monotone"
                        dataKey={h.key}
                        stroke="transparent"
                        strokeWidth={14}
                        dot={false}
                        activeDot={false}
                        legendType="none"
                        connectNulls
                        isAnimationActive={false}
                        onClick={() => togglePin(h.key)}
                        style={{ cursor: "pointer" }}
                      />
                    )
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {/* Accessible table view: average per hospital for the selected period. */}
        {rows.length > 0 && (
          <details className="mt-3 text-xs text-gray-600">
            <summary className="cursor-pointer select-none font-medium">{t("compliance.showTable", "Show as table")}</summary>
            <div className="mt-2 max-h-72 overflow-auto">
              <table className="min-w-full">
                <thead className="text-left text-gray-500">
                  <tr>
                    <th className="py-1 pr-4">{t("compliance.period", "Period")}</th>
                    {showTotal && <th className="py-1 pr-4 text-right">{t("compliance.allHospitalsTotal", "All hospitals")}</th>}
                    {visibleHospitals.map((h) => (
                      <th key={h.key} className="whitespace-nowrap py-1 pr-4 text-right">{h.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...rows].reverse().map((r: any) => (
                    <tr key={r.ts} className="border-t border-gray-100">
                      <td className="whitespace-nowrap py-1 pr-4">{tipFmt.format(new Date(r.ts))}</td>
                      {showTotal && <td className="py-1 pr-4 text-right tabular-nums">{fmtPct(r.total)}</td>}
                      {visibleHospitals.map((h) => (
                        <td key={h.key} className="py-1 pr-4 text-right tabular-nums">{fmtPct(r[h.key])}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </div>
    </Section>
  );
}

/** Legend entry: colour swatch + name. Click toggles the line; hover highlights it. */
function LegendChip({
  label,
  color,
  off,
  thick = false,
  onClick,
  onHover,
}: {
  label: string;
  color: string;
  off: boolean;
  thick?: boolean;
  onClick: () => void;
  onHover: () => void;
}) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={onHover}
      aria-pressed={!off}
      className={cn(
        "flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors",
        off ? "border-gray-200 bg-white text-gray-400" : "border-gray-200 bg-gray-50 text-gray-800 hover:bg-gray-100"
      )}
    >
      <span
        className="inline-block w-4"
        style={{ height: thick ? 3 : 2, backgroundColor: off ? "#d1d5db" : color }}
        aria-hidden
      />
      <span className={cn(thick && "font-semibold", off && "line-through")}>{label}</span>
    </button>
  );
}

/** Compact live tile for Live Activity: current 2h rate, refreshed every minute. */
export function LiveComplianceStrip({ hospitalId }: { hospitalId?: string }) {
  const [rate, setRate] = useState<ComplianceRate | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        // limit=1: only the rate and counts are needed here, not the rows.
        const res = await fetchComplianceStatus({ hospitalId, status: "overdue", limit: 1 });
        if (alive) {
          setRate(res.rate ?? null);
          setFailed(false);
        }
      } catch {
        if (alive) setFailed(true);
      }
    };
    void load();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, 60_000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [hospitalId]);

  if (failed && !rate) return null;
  return <ComplianceRateSummary rate={rate} compact linkTo="/supervisor/compliance" />;
}
